import { servicePost } from "./client.ts";
import type { KioskMapLocation, LocationMapDatabasePosition } from "../types/locationMap.ts";

/** Fetch all kiosk map locations for the current endpoint. */
export async function getGlobalKioskMapLocations(): Promise<KioskMapLocation[]> {
  const result = await servicePost<KioskMapLocation[]>("WebGetGlobalKioskMapLocations");
  if (result.bError) {
    throw new Error(result.sMessage || "Failed to load map locations");
  }
  return result.oData ?? [];
}

export async function getLocationMapDatabasePosition(): Promise<LocationMapDatabasePosition | null> {
  const result = await servicePost<LocationMapDatabasePosition>("WebGetLocationMapDatabasePosition", {});
  if (result.bError) {
    throw new Error(result.sMessage || "Failed to load database map position");
  }
  return result.oData ?? null;
}

export interface KioskRecentSale {
  TransactionID: number;
  EndpointID: number;
  KioskID: number;
  SaleCount: number;
  Amount: number;
  CurrencySymbol?: string;
  CustomerName?: string;
  Country?: string;
  Created?: string;
  /** Non-revenue activity carried by the Tools/AIM live stream. */
  ToolCount?: number;
}

export interface RecentKioskSalesResult {
  Watermark: number;
  Sales: KioskRecentSale[];
  ActiveKiosks?: number;
  Sales5m?: number;
}

/** Poll for transactions created since the given id watermark (0 = seed only,
 *  returns just the current watermark). Scoped server-side to the active
 *  endpoint; rides on the map's "Live" tracking to ping kiosks as they sell. */
export async function getRecentKioskSales(sinceId: number): Promise<RecentKioskSalesResult> {
  const result = await servicePost<RecentKioskSalesResult>("WebGetRecentKioskSales", { sinceId });
  if (result.bError) {
    throw new Error(result.sMessage || "Failed to load recent kiosk sales");
  }
  return result.oData ?? { Watermark: sinceId, Sales: [] };
}

export interface ToolsTransactionWatermark {
  EndpointID: number;
  TransactionID: number;
}

export interface ToolsTransactionActivity {
  TransactionID: number;
  EndpointID: number;
  KioskID: number;
  ToolCount?: number;
  Created?: string;
}

export interface ToolsTransactionActivityResult {
  Watermarks: ToolsTransactionWatermark[];
  Transactions: ToolsTransactionActivity[];
}

/** Poll AIM transaction heads across the five Tools databases. The first call only
 * seeds one id watermark per database, so old activity is never replayed. */
export async function getRecentToolsTransactions(
  watermarks: ToolsTransactionWatermark[],
): Promise<ToolsTransactionActivityResult> {
  const result = await servicePost<ToolsTransactionActivityResult>("WebGetRecentToolsTransactions", { watermarks });
  if (result.bError) {
    throw new Error(result.sMessage || "Failed to load recent Tools transactions");
  }
  return result.oData ?? { Watermarks: watermarks, Transactions: [] };
}

export interface SalesRollupSummary {
  HourSek: number;
  AmountSek: number;
  YesterdaySek: number;
  LastWeekSameTimeSek: number;
  /** Last week, same day, the clock hour that is in progress right now — pro-rated by the
   *  caller to compare against the same moment rather than the last full hour. Absent on
   *  servers that predate it. */
  LastWeekHourSek?: number;
  MonthSek: number;
  SaleCount: number;
  ActiveKiosks?: number;
  History?: SalesRollupPoint[];
  /** Today's top-earning stores in SEK, best first — [0] wears the king's crown. */
  TopStores?: KioskDaySales[];
  /** Today's top-earning non-store kiosks, best first — [0] wears the queen's crown. */
  TopKiosks?: KioskDaySales[];
  /** Yesterday's store podium over the whole UTC day, best first. */
  YesterdayTopStores?: KioskDaySales[];
  /** Yesterday's non-store kiosk podium over the whole UTC day, best first. */
  YesterdayTopKiosks?: KioskDaySales[];
  /** Currency the totals should be labelled with; "SEK" when no single one dominates. */
  DisplayCurrency?: string;
  /** Hour/today/month in DisplayCurrency. Equal to the *Sek fields when that is SEK. */
  HourNative?: number;
  AmountNative?: number;
  MonthNative?: number;
  Currencies?: SalesRollupCurrency[];
}

/** One currency's share of the scope's money over the summary window. */
export interface SalesRollupCurrency {
  Currency: string;
  HourNative: number;
  AmountNative: number;
  MonthNative: number;
  SaleCount: number;
  WindowNative: number;
  WindowSek: number;
}

/** One kiosk's sales so far today (UTC day). Valued in SEK server-side so kiosks in
 *  different currencies can be ranked against each other. */
export interface KioskDaySales {
  EndpointID: number;
  KioskID: number;
  SaleCount: number;
  AmountNative: number;
  CurrencySymbol?: string;
  AmountSek: number;
  Country?: string;
  CustomerName?: string;
  /** Kiosk model — free text, shown but never classified on. */
  KioskType?: string;
  /** The kiosk's CustomerID setting ("Store", "Cooler", …) — what splits the two columns. */
  CustomerId?: string;
  /** Store/location label so identical customers on the podium can be told apart. */
  StoreName?: string;
}

export interface SalesRollupPoint {
  PeriodType: "day" | "month";
  PeriodStart: string;
  AmountSek: number;
  /** Same period in the summary's DisplayCurrency; 0 when that is SEK. */
  AmountNative?: number;
  SaleCount: number;
  IsComplete?: boolean;
}

/** Persisted "today so far" totals (Swedish local day) from the S_Sales_Rollup
 *  collector — SEK on the server's FX, survives refresh. Scoped server-side to the
 *  active endpoint set (global endpoint 26 = all). SuperAdmin-only (revenue). */
const SALES_ROLLUP_TIMEOUT_MS = 30_000;

export async function getSalesRollupToday(): Promise<SalesRollupSummary | null> {
  // Callers share one in-flight request, so a request that never settles would freeze every later poll.
  const result = await servicePost<SalesRollupSummary>("WebGetSalesRollup", {}, AbortSignal.timeout(SALES_ROLLUP_TIMEOUT_MS));
  if (result.bError) {
    throw new Error(result.sMessage || "Failed to load sales rollup");
  }
  return result.oData ?? null;
}

/** One endpoint's LIVE kiosks, split by whether the map can draw them. */
export interface KioskMapCoverageRow {
  EndpointID: number;
  CustomerName?: string;
  /** Has a coordinate and has reported activity in the last 24h — a drawn marker. */
  OnMap: number;
  MissingCoordinate: number;
}

export interface KioskMapCoverageResult {
  OnMap: number;
  MissingCoordinate: number;
  /** Only endpoints that are actually missing coordinates. */
  Rows: KioskMapCoverageRow[];
}

/** Count the LIVE kiosks the map cannot place, over the same endpoint scope and the
 *  same joins the map itself uses. Cheap enough to fetch on demand, so it rides the
 *  Issues panel rather than every map load. */
export async function getKioskMapCoverage(): Promise<KioskMapCoverageResult> {
  const result = await servicePost<KioskMapCoverageResult>("WebGetKioskMapCoverage", {});
  if (result.bError) {
    throw new Error(result.sMessage || "Failed to load map coverage");
  }
  return result.oData ?? { OnMap: 0, MissingCoordinate: 0, Rows: [] };
}

/** One kiosk's AIM transaction counts: running UTC day, yesterday, running UTC month. */
export interface ToolsKioskEventCount {
  EndpointID: number;
  KioskID: number;
  TodayCount: number;
  YesterdayCount: number;
  MonthCount: number;
  /** AIM endpoint walls only; null on ToolsXO. */
  TodayItems?: number | null;
  YesterdayItems?: number | null;
  MonthItems?: number | null;
}

export interface ToolsKioskEventsResult {
  Rows: ToolsKioskEventCount[];
}

/** Transaction counts per kiosk over the caller's AIM scope (ToolsXO's five, or an
 *  AIM endpoint's own on the Admin Map), same UTC day as the sales
 *  wall. Ranking per country is done client-side, where the map's own country codes live.
 *  Cached server-side for a minute, so every wall screen shares one computation. */
export async function getToolsKioskEvents(): Promise<ToolsKioskEventsResult> {
  const result = await servicePost<ToolsKioskEventsResult>("WebGetToolsKioskEvents", {});
  if (result.bError) {
    throw new Error(result.sMessage || "Failed to load Tools kiosk events");
  }
  return result.oData ?? { Rows: [] };
}

